import { useEffect, useRef, useState } from "react";

// variant: "up" (default) | "left" | "right" | "zoom" | "fade" | "clip"
//   Controls the entrance motion — the .reveal--<variant> class picks the
//   matching from-state in CSS. All settle to the same visible end-state.
export default function Reveal({
  as: Tag = "div",
  delay = 0,
  threshold = 0.15,
  variant = "up",
  className = "",
  children,
  ...rest
}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    // Safety net: if the element is already within the viewport on mount
    // (e.g. above/at the fold, or the observer callback is delayed), reveal
    // it immediately so it can never stay stuck at opacity:0 / clipped.
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      setVisible(true);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setVisible(true);
            io.unobserve(e.target);
          }
        });
      },
      { threshold, rootMargin: "0px 0px -8% 0px" }
    );
    io.observe(el);

    // Absolute fallback — never let content stay hidden if the observer
    // silently fails to fire for any reason.
    const t = setTimeout(() => setVisible(true), 1200);
    return () => { io.disconnect(); clearTimeout(t); };
  }, [threshold]);

  return (
    <Tag
      ref={ref}
      className={`reveal reveal--${variant} ${visible ? "is-visible" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
      {...rest}
    >
      {children}
    </Tag>
  );
}
